import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";

export default function TermsPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <a href="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-orange-600 mb-8">
          <ArrowLeft size={14} /> Back to AdBit
        </a>

        <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Terms and Conditions of Use</h1>
        <p className="text-sm text-slate-400 mb-10">Last updated: April 2026</p>

        <div className="prose prose-slate prose-sm max-w-none">
          <Section title="1. Acceptance of Terms">
            <p>By accessing or using the AdBit platform ("Platform"), available at www.adbit.digital, you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree with any part of these Terms, you must not use the Platform.</p>
            <p>These Terms constitute a legally binding agreement between you ("User") and AdBit ("we", "us", "our"), operated by an individual entrepreneur registered in Mexico ("Service Provider"). Full legal identification details are available upon legitimate request or where required by applicable law.</p>
            <p>By creating an account, you confirm that you are at least 18 years of age and have the legal capacity to enter into this agreement. If you are using the Platform on behalf of a company or organization, you represent that you have authority to bind that entity to these Terms.</p>
          </Section>

          <Section title="2. Description of Service">
            <p>AdBit is an AI-powered advertising analysis platform that evaluates static image advertisements using computer vision, machine learning, and neuroscience-based models. The Platform provides predictions, scores, recommendations, and audience segmentation insights.</p>
            <p>These outputs are informational only and are not guarantees of advertising performance. The Platform currently analyzes static image ads only.</p>
          </Section>

          <Section title="3. Account Registration and Security">
            <p>To use the Platform's analysis features, you must create an account by providing accurate information. You are responsible for maintaining the confidentiality of your credentials and for all activities under your account. Unauthorized sharing of account credentials is prohibited.</p>
            <p>We reserve the right to suspend or terminate accounts that violate these Terms, engage in fraudulent activity, abuse the Platform, or remain inactive for more than 12 consecutive months.</p>
          </Section>

          <Section title="4. Subscription, Billing, and Pricing">
            <h4 className="font-semibold text-slate-700 mt-4">4.1 Plans</h4>
            <p>AdBit offers a free Trial plan and paid subscription plans. Paid plans are available in US Dollars (USD) and Mexican Pesos (MXN) with fixed prices for each currency. Prices in USD and MXN are set independently and do not reflect exact currency conversion rates.</p>

            <h4 className="font-semibold text-slate-700 mt-4">4.2 Automatic Renewal</h4>
            <p>Subscriptions automatically renew unless cancelled before the renewal date. We will send a reminder notification at least 5 calendar days before each automatic renewal.</p>

            <h4 className="font-semibold text-slate-700 mt-4">4.3 Price Changes</h4>
            <p>Price increases require your express consent. We will notify you at least 30 days in advance.</p>

            <h4 className="font-semibold text-slate-700 mt-4">4.4 Upgrades and Downgrades</h4>
            <p>Plan changes take effect at the beginning of the next billing cycle. No prorated credits or refunds are issued for downgrades.</p>

            <h4 className="font-semibold text-slate-700 mt-4">4.5 Cancellation</h4>
            <p>You may cancel at any time through the "Manage Subscription" button in Settings. Cancellation is immediate, simple, and free of penalties. Access continues until the end of the billing period.</p>

            <h4 className="font-semibold text-slate-700 mt-4">4.6 Refund Policy</h4>
            <p><strong>To the extent permitted by applicable law, all sales are final.</strong> Exception: Full refund within 5 business days of initial subscription only if no usage occurred.</p>

            <h4 className="font-semibold text-slate-700 mt-4">4.7 Chargebacks</h4>
            <p>Initiating a chargeback without first contacting us may result in immediate account suspension. Contact support@adbit.digital to resolve billing concerns.</p>
          </Section>

          <Section title="5. Nature of Results — AI Disclaimer">
            <p><strong>ALL OUTPUTS ARE COMPUTATIONAL PREDICTIONS GENERATED BY AI. THEY ARE NOT GUARANTEES OF ADVERTISING PERFORMANCE.</strong></p>
            <p>AI-generated content may contain errors. Users must independently verify results before relying on them for business decisions.</p>
          </Section>

          <Section title="6. No Professional Advice">
            <p>The Platform does not provide legal, regulatory, tax, financial, or any other form of professional advice. Users are solely responsible for validating claims before public use. AdBit is not liable for consequences arising from use of Platform outputs without independent verification.</p>
          </Section>

          <Section title="7. User Responsibility and Compliance">
            <p>Users must ensure compliance with all applicable laws when using the Platform and its outputs, including advertising standards, consumer protection, and intellectual property laws.</p>
          </Section>

          <Section title="8. Intellectual Property">
            <p><strong>User Content:</strong> You retain ownership of uploaded content. You grant AdBit a limited license to process it for analysis.</p>
            <p><strong>Third-Party Content:</strong> If you upload content containing third-party images, trademarks, or personal data, you are responsible for having the necessary rights.</p>
            <p><strong>Platform Improvement:</strong> AdBit may use anonymized, aggregated data (such as category benchmarks) to improve scoring accuracy. This is an optional purpose; you may object via support@adbit.digital.</p>
            <p><strong>Platform Ownership:</strong> All platform technology, algorithms, and models are owned by AdBit. Reverse-engineering is prohibited.</p>
          </Section>

          <Section title="9. Acceptable Use">
            <p>You may not upload illegal content, reverse-engineer the platform, bypass limits, generate deceptive advertising, use excessive automated tools, or upload malware.</p>
          </Section>

          <Section title="10. Use by Agencies">
            <p>You may use outputs to provide services to clients. You may not resell the Platform, sublicense outputs as a standalone product, or offer AdBit as a white-label solution without written consent.</p>
          </Section>

          <Section title="11. Third-Party Services">
            <p>We use Google Cloud Vision, Anthropic Claude, Stripe, Gigstack, MongoDB Atlas, Vercel, Railway, Google Analytics (G-C1MJ333LKE), Meta Pixel/Conversion API (1316242720526518), Microsoft Clarity (wfvs98lwr1), and Resend. Each is governed by their own policies.</p>
          </Section>

          <Section title="12. Service Availability">
            <p>AdBit uses commercially reasonable efforts to maintain availability. No guaranteed uptime is provided unless separately agreed.</p>
          </Section>

          <Section title="13. Termination">
            <p>AdBit may suspend or terminate access for cause with reasonable notice (minimum 15 days), except in cases of fraud or security threats. Upon termination, you have 30 days to export your data.</p>
          </Section>

          <Section title="14. Limitation of Liability">
            <p><strong>To the maximum extent permitted by law, AdBit shall not be liable for indirect, incidental, special, consequential, or punitive damages.</strong> Total liability shall not exceed fees paid in the previous 12 months.</p>
          </Section>

          <Section title="15. Indemnification">
            <p>You agree to indemnify AdBit from claims arising from your use of the Platform, violation of Terms, use of outputs, or third-party claims.</p>
          </Section>

          <Section title="16. Export Control">
            <p>The Platform may not be used by entities subject to international sanctions (OFAC, EU, UN).</p>
          </Section>

          <Section title="17. Assignment">
            <p>AdBit may transfer rights and obligations to a legal entity as part of restructuring without individual user consent.</p>
          </Section>

          <Section title="18. Modifications">
            <p>We may update these Terms with at least 15 days notice. Continued use constitutes acceptance.</p>
          </Section>

          <Section title="19. Severability">
            <p>If any provision is found unenforceable, the remaining provisions continue in full force.</p>
          </Section>

          <Section title="20. Governing Law">
            <p>Governed by the laws of Mexico. Disputes subject to courts in Mexico City unless otherwise required by law.</p>
          </Section>

          <Section title="21. Contact">
            <p>support@adbit.digital</p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <h3 className="text-base font-bold text-slate-800 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h3>
      <div className="text-sm text-slate-600 leading-relaxed space-y-2">{children}</div>
    </div>
  );
}
